import React, { Component } from "react";
import styled from "styled-components";
import { app } from "js/namespaces";

import { AiImagePresetStyles } from "../../../../common/aiConstants";
import { ActionPermissionsObject, WorkspacePlanTier } from "../../../../common/interfaces/index";
import { Button } from "../../../Components/Button";
import { Dropdown } from "../../../Components/Dropdown";
import { Icon } from "../../../Components/Icon";
import { ImageOption, ImageOptionList } from "../../../Components/ImageOptionList";
import { MenuItemDetails, MenuItemWithDetails } from "../../../Components/Menu";
import { Popup, PopupContainer, PopupContent, PopupPreview } from "../../../Components/Popup";
import { TextField } from "../../../Components/TextField";
import { WithLabel } from "../../../Components/WithLabel";
import WorkspaceController from "../../../controllers/WorkspaceController";
import { FlexSpacer, Gap10 } from "../../../react/components/Gap";
import { FlexBox } from "../../../react/components/LayoutGrid";
import { SimpleLabel } from "../../../react/components/SimpleLabel";
import { AiIconCircle } from "../AiIcon";
import { t } from "js/i18n";

class AiImageStyle extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedImageStyle: props.imageStyle,
            imageStylePrompt: this.getImageStylePrompt(props.imageStyle),
        };
    }

    componentDidUpdate(prevProps) {
        const { imageStyle } = this.props;
        if (prevProps.imageStyle === imageStyle) {
            return;
        }

        // Mounted inline in the theme editor, this outlives the changes it makes:
        // clearing the prompt resolves back to a preset, and without re-deriving
        // the grid would keep showing nothing selected. Changes that only echo
        // this component's own state back are left alone, so typing in the popup
        // (which commits per keystroke) isn't disturbed.
        const { selectedImageStyle, imageStylePrompt } = this.state;
        if (imageStyle === selectedImageStyle || this.getImageStylePrompt(imageStyle) === imageStylePrompt) {
            return;
        }

        this.setState({
            selectedImageStyle: imageStyle,
            imageStylePrompt: this.getImageStylePrompt(imageStyle)
        });
    }

    getImageStylePrompt(imageStyle) {
        const { theme } = this.props;
        if (imageStyle === "**theme**") {
            imageStyle = theme.getAiImageStyle();
        }
        if (AiImagePresetStyles.find(style => style.style === imageStyle)) {
            return AiImagePresetStyles.find(style => style.style === imageStyle).prompt;
        }
        return imageStyle;
    }

    handleSetImageStyle = imageStyle => {
        const { onChange } = this.props;
        this.setState({
            selectedImageStyle: imageStyle,
            imageStylePrompt: this.getImageStylePrompt(imageStyle),
        });
        if (onChange) {
            if (imageStyle === "**theme**" || AiImagePresetStyles.find(style => style.style === imageStyle)) {
                onChange(imageStyle);
            } else {
                onChange(this.getImageStylePrompt(imageStyle));
            }
        }
    }

    handleSetImageStylePrompt = imageStylePrompt => {
        const { onChange, commitOnBlur } = this.props;
        this.setState({ selectedImageStyle: "custom", imageStylePrompt });
        // When the change is applied straight to a model rather than staged
        // behind an Apply button, defer to blur so typing doesn't produce a
        // change (and an undo step) per keystroke.
        if (onChange && !commitOnBlur) {
            onChange(imageStylePrompt);
        }
    }

    handleCommitImageStylePrompt = imageStylePrompt => {
        const { onChange, commitOnBlur, imageStyle } = this.props;
        if (!onChange || !commitOnBlur) {
            return;
        }

        // Blur fires even when nothing was typed. Committing an unchanged value
        // would be read as a custom prompt and silently turn a named preset into
        // a custom style, dirtying the theme for a click-through.
        if (imageStylePrompt === this.getImageStylePrompt(imageStyle)) {
            return;
        }

        onChange(imageStylePrompt);
    }

    render() {
        // 90 rather than 120: at two dozen styles the grid was the tallest thing in every pane that
        // carries it. The labels are one or two short words, so they still fit on a line.
        let { thumbnailSize = 90, hideThemeImageStyle = false, singleRow = false, promptLabel, disabled = false } = this.props;
        const { selectedImageStyle, imageStylePrompt } = this.state;

        let cols = 6;
        let illustrativeCols = 7;
        if (app.isMobileOrTablet) {
            thumbnailSize = 60;
            cols = 6;
            illustrativeCols = 6;
        }
        // One scrolling row per group rather than a wrapping grid, for panes too narrow to show a
        // group's full width without stacking it several rows deep.
        let photoCols = cols;
        if (singleRow) {
            photoCols = AiImagePresetStyles.filter(s => s.type == "photo").length;
            illustrativeCols = AiImagePresetStyles.filter(s => s.type == "illustration").length;
        }

        return (
            <FlexBox vertical left gap={10} style={{ overflow: "hidden" }}>
                <FlexBox vertical left gap={6} fillWidth>
                    <FlexBox left top gap={40} fillWidth style={{ overflowX: "auto", overflowY: "hidden", padding: "2px 8px" }}>
                        {!hideThemeImageStyle && (
                            <FlexBox left vertical width={100} gap={0}>
                                <ImageStyleGroupLabel>{t("Use Theme Style")}</ImageStyleGroupLabel>
                                <ImageOptionList cols={cols} gap={10} size={thumbnailSize} onChange={this.handleSetImageStyle}>
                                    <ImageOption
                                        label={t("Theme Style")}
                                        value="**theme**"
                                        url={`/images/ai-image-styles/theme.svg`}
                                        selected={selectedImageStyle === "**theme**"}
                                        disabled={disabled}
                                    />
                                </ImageOptionList>
                            </FlexBox>
                        )}
                        <FlexBox left vertical gap={0}>
                            <ImageStyleGroupLabel>{t("PHOTOGRAPHIC")}</ImageStyleGroupLabel>
                            <ImageOptionList cols={photoCols} gap={10} size={thumbnailSize} onChange={this.handleSetImageStyle}>
                                {AiImagePresetStyles.filter(s => s.type == "photo").map((style, idx) => (
                                    <ImageOption
                                        key={idx}
                                        label={t(style.label)}
                                        url={`/images/ai-image-styles/${style.icon}`}
                                        value={style.style}
                                        selected={selectedImageStyle === style.style}
                                        disabled={disabled}
                                    />
                                ))}
                            </ImageOptionList>
                        </FlexBox>
                    </FlexBox>
                    <FlexBox left vertical gap={0} fillWidth style={{ overflowX: "auto", padding: "2px 8px" }}>
                        <ImageStyleGroupLabel $compact>{t("ILLUSTRATIVE")}</ImageStyleGroupLabel>
                        <ImageOptionList cols={illustrativeCols} gap={10} size={thumbnailSize} onChange={this.handleSetImageStyle}>
                            {AiImagePresetStyles.filter(s => s.type == "illustration").map((style, idx) => (
                                <ImageOption
                                    key={idx}
                                    label={t(style.label)}
                                    url={`/images/ai-image-styles/${style.icon}`}
                                    value={style.style}
                                    selected={selectedImageStyle === style.style}
                                    disabled={disabled}
                                />
                            ))}
                        </ImageOptionList>
                    </FlexBox>
                </FlexBox>
                <FlexBox left vertical gap={0} fillWidth>
                    {/* The popup uses the app's uppercase label; panes that lay the
                        field out as a form pass their own sentence-case one. */}
                    {promptLabel
                        ? <PlainSimpleLabel>{promptLabel}</PlainSimpleLabel>
                        : <SimpleLabel>{t("IMAGE STYLE PROMPT")}</SimpleLabel>}
                    <TextField variant="outlined" multiline fullWidth
                        value={imageStylePrompt} onChange={value => this.handleSetImageStylePrompt(value)}
                        onBlur={value => this.handleCommitImageStylePrompt(value)}
                        style={{ background: "white", userSelect: "text" }}
                        disabled={disabled}
                    />
                </FlexBox>
            </FlexBox>
        );
    }
}

class AiImageStylePopup extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedImageStyle: props.imageStyle,
            imageModel: props.imageModel
        };
    }

    handleApply = closePopup => {
        let { onChange } = this.props;
        let { selectedImageStyle, imageModel } = this.state;
        onChange(selectedImageStyle, imageModel);
        closePopup();
    };

    handleCancel = closePopup => {
        this.setState({ selectedImageStyle: this.props.imageStyle });
        closePopup();
    }

    render() {
        let { previewSize = 83, imageStyle, theme, hideThemeImageStyle = false } = this.props;
        let { selectedImageStyle } = this.state;
        const isBasic = WorkspaceController.plan.tier === WorkspacePlanTier.BASIC;
        const popupDisabled = isBasic;

        let preview;
        if (imageStyle === "**theme**") {
            preview = (
                <ImageOption
                    disabled={popupDisabled}
                    label={t("Theme Style")}
                    url={`/images/ai-image-styles/theme.svg`}
                    size={previewSize}
                />
            );
        } else if (AiImagePresetStyles.find(style => style.style === imageStyle)) {
            let presetStyle = AiImagePresetStyles.find(style => style.style === imageStyle);
            preview = (
                <ImageOption
                    disabled={popupDisabled}
                    label={t(presetStyle.label)}
                    url={`/images/ai-image-styles/${presetStyle.icon}`}
                    size={previewSize}
                />
            );
        } else {
            preview = (
                <ImageOption
                    disabled={popupDisabled}
                    label={t("Custom")}
                    url={`/images/ai-image-styles/custom.svg`}
                    size={previewSize}
                />
            );
        }

        return (
            <Popup disabled={popupDisabled}>
                <PopupPreview>
                    {preview}
                </PopupPreview>
                <PopupContent allowFocus>
                    {closePopup => (
                        <PopupContainer>
                            <AiImageStyle
                                theme={theme}
                                imageStyle={selectedImageStyle}
                                onChange={selectedImageStyle => this.setState({ selectedImageStyle })}
                                hideThemeImageStyle={hideThemeImageStyle}
                            />
                            <FlexBox right gap={10}>
                                {/*<WithLabel label="Model">*/}
                                {/*    <AiImageModelPicker imageModel={imageModel} onChange={imageModel => this.setState({ imageModel })} />*/}
                                {/*</WithLabel>*/}

                                <FlexSpacer />

                                <Button unframed onClick={() => this.handleCancel(closePopup)}>{t("Cancel")}</Button>
                                <Button blue onClick={() => this.handleApply(closePopup)}>{t("Apply Style")}</Button>
                            </FlexBox>
                            <Gap10 />
                        </PopupContainer>
                    )}
                </PopupContent>
            </Popup>
        );
    }
}

const PlainSimpleLabel = styled(SimpleLabel)`
    text-transform: none;
`;

// The headings over the thumbnail groups name what is below them rather than
// labelling a control, so they sit lighter and smaller than the app's default
// label while keeping the semi-bold weight the app's labels are set in.
const ImageStyleGroupLabel = styled(SimpleLabel)`
    color: #666;
    font-size: 11px;
    letter-spacing: 0.5px;
    ${({ $compact }) => $compact && "margin-top: 2px;"}
`;

const IconContainer = styled.div`
    width: 85px;
    height: 62px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: solid 1px #ddd;
`;

class AiImageSourcePopup extends Component {
    render() {
        const {
            imageSource,
            onChange,
            label = t("Image Source"),
            toolTip,
            noneLabel = t("No Images"),
            hideTeamAssets = false,
            dataTestId = "gen-ai-image-source"
        } = this.props;
        const canGenerate = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI_IMAGES].use;
        const canUseWebSearch = WorkspaceController.actionPermissions[ActionPermissionsObject.WEB_IMAGES].use;
        const canUseStockImages = WorkspaceController.actionPermissions[ActionPermissionsObject.STOCK_LIBRARIES].use;
        const canUseTeamAssets = !hideTeamAssets && WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;

        let imageSourceLabel;
        if (imageSource == "ai") {
            imageSourceLabel = t("AI Generated Images");
        } else if (imageSource == "team") {
            imageSourceLabel = t("Team Assets");
        } else if (imageSource == "web") {
            imageSourceLabel = t("Web Search");
        } else if (imageSource == "stock") {
            imageSourceLabel = t("Stock Images");
        } else if (imageSource == "none") {
            imageSourceLabel = noneLabel;
        }

        return (
            <WithLabel label={label} toolTip={toolTip} above data-test-id={dataTestId}>
                <Popup>
                    <PopupPreview>
                        <WithLabel below center gap={7} label={imageSourceLabel} small tight>
                            <IconContainer>
                                {imageSource == "ai" && <AiIconCircle size={34} />}
                                {imageSource == "team" && <Icon size={34}>groups</Icon>}
                                {imageSource == "web" && <Icon size={34}>public</Icon>}
                                {imageSource == "stock" && <Icon size={34}>image</Icon>}
                                {imageSource == "none" && <Icon size={34}>block</Icon>}
                            </IconContainer>
                        </WithLabel>
                    </PopupPreview>
                    <PopupContent>
                        {closePopup => (
                            <>
                                {canGenerate && (
                                    <MenuItemWithDetails value="ai" icon={<AiIconCircle />}
                                        onClick={() => {
                                            onChange("ai");
                                            closePopup();
                                        }}>
                                        {t("AI Generated Images")}
                                        <MenuItemDetails>{t("Use AI to generate custom images based on your presentation content.")}</MenuItemDetails>
                                    </MenuItemWithDetails>
                                )}
                                {canUseTeamAssets && (
                                    <MenuItemWithDetails value="team" icon={<Icon>groups</Icon>}
                                        onClick={() => {
                                            onChange("team");
                                            closePopup();
                                        }}>
                                        {t("Team Assets")}
                                        <MenuItemDetails>{t("Use images from your team's shared asset library.")}</MenuItemDetails>
                                    </MenuItemWithDetails>
                                )}
                                {canUseWebSearch && (
                                    <MenuItemWithDetails value="web" icon={<Icon>public</Icon>}
                                        onClick={() => {
                                            onChange("web");
                                            closePopup();
                                        }}>
                                        {t("Web Search")}
                                        <MenuItemDetails>{t("Search the web for relevant images to include in your presentation.")}</MenuItemDetails>
                                    </MenuItemWithDetails>
                                )}
                                {canUseStockImages && (
                                    <MenuItemWithDetails value="stock" icon={<Icon>image</Icon>}
                                        onClick={() => {
                                            onChange("stock");
                                            closePopup();
                                        }}>
                                        {t("Stock Images")}
                                        <MenuItemDetails>{t("Use high-quality stock images from our library.")}</MenuItemDetails>
                                    </MenuItemWithDetails>
                                )}
                                <MenuItemWithDetails value="none" icon={<Icon>block</Icon>}
                                    onClick={() => {
                                        onChange("none");
                                        closePopup();
                                    }}>
                                    {noneLabel}
                                    <MenuItemDetails>{t("Do not include any images in the generated presentation.")}</MenuItemDetails>
                                </MenuItemWithDetails>
                            </>
                        )}
                    </PopupContent>
                </Popup>
            </WithLabel>
        );
    }
}

class AIImageSourcePicker extends Component {
    render() {
        const { value, onChange, disabled = false, hideTeamAssets = false } = this.props;
        const canGenerate = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI_IMAGES].use;
        const canUseWebSearch = WorkspaceController.actionPermissions[ActionPermissionsObject.WEB_IMAGES].use;
        const canUseStockImages = WorkspaceController.actionPermissions[ActionPermissionsObject.STOCK_LIBRARIES].use;
        const canUseTeamAssets = !hideTeamAssets && WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;

        return (
            <Dropdown value={value} onChange={onChange} disabled={disabled}>
                {canGenerate && (
                    <MenuItemWithDetails value="ai" icon={<AiIconCircle />}
                        onClick={() => {
                            onChange("ai");
                        }}>
                        {t("AI Generated Images")}
                        <MenuItemDetails>{t("Use AI to generate custom images based on your content and prompt.")}</MenuItemDetails>
                    </MenuItemWithDetails>
                )}
                {canUseTeamAssets && (
                    <MenuItemWithDetails value="team" icon={<Icon>groups</Icon>}
                        onClick={() => {
                            onChange("team");
                        }}>
                        {t("Team Assets")}
                        <MenuItemDetails>{t("Use images from your team's shared asset library.")}</MenuItemDetails>
                    </MenuItemWithDetails>
                )}
                {canUseWebSearch && (
                    <MenuItemWithDetails value="web" icon={<Icon>public</Icon>}
                        onClick={() => {
                            onChange("web");
                        }}>
                        {t("Web Search")}
                        <MenuItemDetails>{t("Search the web for relevant images.")}</MenuItemDetails>
                    </MenuItemWithDetails>
                )}
                {canUseStockImages && (
                    <MenuItemWithDetails value="stock" icon={<Icon>image</Icon>}
                        onClick={() => {
                            onChange("stock");
                        }}>
                        {t("Stock Images")}
                        <MenuItemDetails>{t("Use high-quality stock images from our library.")}</MenuItemDetails>
                    </MenuItemWithDetails>
                )}
                <MenuItemWithDetails value="none" icon={<Icon>block</Icon>}
                    onClick={() => {
                        onChange("none");
                    }}>
                    {t("Placeholders (No Images)")}
                    <MenuItemDetails>{t("Create placeholders that you can quickly replace with your own images.")}</MenuItemDetails>
                </MenuItemWithDetails>
            </Dropdown>
        );
    }
}

export { AiImageStyle, AiImageStylePopup, AiImageSourcePopup, AIImageSourcePicker };
